import { DirectUpload } from "@rails/activestorage"

/**
 * DirectUploadManager handles uploading files directly to S3
 * Returns signed blob IDs that can be attached to records
 */
export class DirectUploadManager {
  constructor(options = {}) {
    this.url = options.url || "/rails/active_storage/direct_uploads"
    this.onProgress = options.onProgress || (() => {})
    this.onError = options.onError || ((error) => console.error(error))
  }

  /**
   * Upload a single file directly to S3
   * @param {File} file - The file to upload
   * @returns {Promise<string>} - The signed blob ID
   */
  uploadFile(file) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, this.url, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const progress = (event.loaded / event.total) * 100
              this.onProgress(file, progress)
            }
          })
        }
      })

      upload.create((error, blob) => {
        if (error) {
          this.onError(error)
          reject(error)
        } else {
          // Return the signed_id which can be used to attach the blob
          resolve(blob.signed_id)
        }
      })
    })
  }

  /**
   * Upload multiple files directly to S3
   * @param {File[]} files - Array of files to upload
   * @returns {Promise<string[]>} - Array of signed blob IDs
   */
  async uploadFiles(files) {
    const signedIds = []
    for (const file of files) {
      const signedId = await this.uploadFile(file)
      signedIds.push(signedId)
    }
    return signedIds
  }
}

// Export a factory function for easy use
export function createDirectUploadManager(options) {
  return new DirectUploadManager(options)
}

// Make available globally for Alpine.js components
window.DirectUploadManager = DirectUploadManager
window.createDirectUploadManager = createDirectUploadManager
